import React, { useState, useEffect } from 'react';
import './ImageModal.css';

function ImageModal({ asset, onClose }) {
    const [convertedUrl, setConvertedUrl] = useState(asset?.url || '');

    useEffect(() => {
        const loadImage = async () => {
            if (!asset?.url) return;
            
            try {
                let imageUrl = asset.url;

                // Handle HCS URLs
                if (imageUrl.startsWith('hcs://')) {
                    const match = imageUrl.match(/hcs:\/\/(\d+)\/(\d+\.\d+\.\d+)/);
                    if (match) {
                        const topicId = match[2];
                        console.log('Fetching HCS content:', { topicId });
                        
                        const response = await fetch(`https://kiloscribe.com/api/inscription-cdn/${topicId}`);
                        const content = await response.text();
                        console.log('Received HCS content:', { contentPreview: content.substring(0, 100) });
                        
                        try {
                            // Try to parse as JSON first
                            const metadata = JSON.parse(content);
                            console.log('Parsed metadata:', metadata);
                            
                            if (metadata.image) {
                                if (metadata.image.startsWith('hcs://')) {
                                    // Handle nested HCS URL
                                    const imageMatch = metadata.image.match(/hcs:\/\/(\d+)\/(\d+\.\d+\.\d+)/);
                                    if (imageMatch) {
                                        const imageTopicId = imageMatch[2];
                                        console.log('Fetching nested image:', { imageTopicId });
                                        const imageResponse = await fetch(`https://kiloscribe.com/api/inscription-cdn/${imageTopicId}`);
                                        const imageContent = await imageResponse.text();
                                        imageUrl = `data:image/svg+xml;base64,${btoa(imageContent)}`;
                                    }
                                } else {
                                    imageUrl = metadata.image;
                                }
                            } else {
                                // If no image field, treat content as direct SVG
                                imageUrl = `data:image/svg+xml;base64,${btoa(content)}`;
                            }
                        } catch (error) {
                            console.log('Not JSON, treating as direct content:', error);
                            // Not JSON, treat as direct SVG content
                            imageUrl = `data:image/svg+xml;base64,${btoa(content)}`;
                        }
                    }
                }

                console.log('Setting converted URL:', imageUrl);
                setConvertedUrl(imageUrl);
            } catch (error) {
                console.error('Error loading image:', error);
            }
        };

        loadImage();
    }, [asset?.url]);

    if (!asset) return null;

    const metadata = {
        // Common fields
        name: asset.metadata?.name || 'Untitled',
        type: asset.type || 'Unknown Type',
        
        // NFT specific fields using correct paths
        tokenId: asset.token_id || 'N/A',
        serialNumber: asset.serial_number || 'N/A',
        description: asset.metadata?.description || 'No description',
        
        // Additional metadata if available
        attributes: asset.metadata?.attributes || [],
        
        // File specific fields (keep these for non-NFT assets)
        size: asset.metadata?.size ? `${(asset.metadata.size / 1024).toFixed(2)} KB` : 'N/A',
        lastModified: asset.metadata?.lastModified ? 
            new Date(asset.metadata.lastModified).toLocaleString() : 'N/A',
            
        // Model specific fields
        isModel: asset.isModel || false,
        modelUrl: asset.modelUrl || asset.url
    };

    return (
        <div className="image-modal-overlay" onClick={onClose}>
            <div className="image-modal-content" onClick={e => e.stopPropagation()}>
                <button className="modal-close-button" onClick={onClose}>×</button>
                
                <div className="modal-image-container">
                    {metadata.isModel ? (
                        <div className="model-preview">
                            <div className="model-icon">🎮</div>
                            <div className="model-label">3D Model</div>
                            <div className="model-url">{metadata.modelUrl}</div>
                        </div>
                    ) : (
                        <img 
                            src={convertedUrl} 
                            alt={metadata.name}
                            className="modal-image"
                        />
                    )}
                </div>

                <div className="modal-info">
                    <h2>{metadata.name}</h2>
                    
                    <div className="metadata-grid">
                        <div className="metadata-section">
                            <h3>NFT Details</h3>
                            <div className="metadata-row">
                                <span className="label">Type:</span>
                                <span className="value">{metadata.isModel ? '3D Model' : '2D Image'}</span>
                            </div>
                            <div className="metadata-row">
                                <span className="label">Token ID:</span>
                                <span className="value">{metadata.tokenId}</span>
                            </div>
                            <div className="metadata-row">
                                <span className="label">Serial #:</span>
                                <span className="value">{metadata.serialNumber}</span>
                            </div>
                            {metadata.description && (
                                <div className="metadata-row">
                                    <span className="label">Description:</span>
                                    <span className="value description">{metadata.description}</span>
                                </div>
                            )}
                        </div>

                        {metadata.attributes && metadata.attributes.length > 0 && (
                            <div className="metadata-section">
                                <h3>Attributes</h3>
                                {metadata.attributes.map((attr, index) => (
                                    <div key={index} className="metadata-row">
                                        <span className="label">{attr.trait_type}:</span>
                                        <span className="value">{attr.value}</span>
                                    </div>
                                ))}
                            </div>
                        )}
                    </div>
                </div>
            </div>
        </div>
    );
}

export default React.memo(ImageModal); 